// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useState, useMemo } from 'react';
import { stringify } from 'query-string';
import { useForm } from 'react-hook-form';
import { omit } from 'lodash';
import { useTranslation } from 'react-i18next';
import { array, func, object } from 'prop-types';

import useRouter from 'Hooks/useRouter';
import useFetch from 'Hooks/useFetch';
import useAsyncError from 'Hooks/useAsyncError';
import useUser from 'Hooks/useUser';
import usePermissions from 'Hooks/usePermissions';
import useScrollLock from 'Hooks/useScrollLock';
import FilterOrdersComponent from 'Components/Orders/FilterOrders';
import useAnalytics from 'Hooks/useAnalytics';
import { API_USERS_LIST } from 'Constants';

const OrdersFilters = memo(({
  sortOptions,
  currentSort,
  handleMobileOrderChange,
}) => {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  const [filterKey, setFilterKey] = useState(0);
  const { t } = useTranslation();
  const { push, location, query } = useRouter();
  const fetcher = useFetch();
  const { data: { loggedIn } } = useUser();
  const { isExternalReseller } = usePermissions();
  const throwError = useAsyncError();
  const analytics = useAnalytics();
  const {
    register,
    control,
    reset,
    watch,
    getValues,
    setValue,
  } = useForm();

  const startDate = watch('start_date');
  const endDate = watch('end_date');

  const states = useMemo(() => [
    { value: '8', label: t('orders.states.canceled') },
    { value: '3', label: t('orders.states.blocked') },
    { value: '0', label: t('orders.states.created') },
    { value: '6', label: t('orders.states.error') },
    { value: '7', label: t('orders.states.expired') },
    { value: '5', label: t('orders.states.done') },
    { value: '1', label: t('orders.states.processing') },
    { value: '4', label: t('orders.states.ready') },
    { value: '2', label: t('orders.states.stockReservation') },
    { value: '9', label: t('orders.states.transit') },
    { value: '10', label: t('orders.states.accepted') },
  ], []);

  const types = useMemo(() => [
    { value: 'offline', label: t('orders.types.offline') },
    { value: 'online', label: t('orders.types.online') },
  ], []);

  const deliveryStockOptions = useMemo(() => [
    { value: 'nod', label: t('products.nodStock') },
    { value: 'supplier', label: t('products.supplierStock') },
  ], []);

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  useEffect(() => {
    if (loggedIn) {
      fetcher(API_USERS_LIST)
        .then(res => {
          const mappedUsers = res.map(user => ({
            value: user.userId,
            label: `${ user?.firstName || '' } ${ user?.lastName || '' }`
          }));
          setUsers(mappedUsers);

          if (Object.keys(omit(query || {}, ['order_by', 'order_direction', 'page'])).length) {
            Object.entries(query).forEach(([key, value]) => {
              setValue(key, value);
            });
          }
        })
        .catch(err => throwError(err))
        .finally(() => {
          setUsersLoading(false);
          setInitialLoadComplete(true);
        });
    }
  }, [loggedIn]);

  // Increment filterKey AFTER component has rendered with initial values
  useEffect(() => {
    if (initialLoadComplete) {
      setFilterKey(prev => prev + 1);
    }
  }, [initialLoadComplete]);

  useScrollLock(mobileFiltersOpen);

  const handleSubmit = () => {
    setMobileFiltersOpen(false);

    const formValues = getValues();

    const queryParams = Object.entries(formValues)
      // eslint-disable-next-line no-unused-vars
      .filter(([_, value]) => value)
      .reduce((acc, [key, value]) => {
        if (value !== 'false') {
          acc[key] = value;
        }
        return acc;
      }, {});

    const searchedValues = Object.keys(queryParams).join(', ');
    analytics.track('filter_orders', { filters: searchedValues });

    push({
      location: location.pathname,
      search: stringify(queryParams)
    });
  };

  const handleClearFilters = () => {
    analytics.track('clear_filters');

    setMobileFiltersOpen(false);
    reset({
      internal_status_id: '',
      user: '',
      order_type: '',
      delivery_stock: '',
      drop_shipment: false,
      product: '',
      reference: '',
      start_date: '',
      end_date: '',
    });

    setFilterKey(prev => prev + 1);

    push({
      location: location.pathname,
      search: ''
    });
  };

  const handleDateChange = (key, dates) => {
    const date = Array.isArray(dates) ? dates[0] : dates;

    if (!date) {
      setValue(key, '');
      return;
    }

    const originalDate = new Date(date);

    if (Number.isNaN(originalDate.getTime())) {
      setValue(key, '');
      return;
    }

    let day = originalDate.getDate();
    let month = originalDate.getMonth() + 1; // Adding 1 because months are zero-indexed
    const year = originalDate.getFullYear();

    // Format day and month to have leading zeros if needed
    day = (day < 10) ? `0${ day }` : day;
    month = (month < 10) ? `0${ month }` : month;

    const formattedDate = `${ year }-${ month }-${ day }`;

    setValue(key, formattedDate);
  };

  const onMobileOrderChange = sort => {
    analytics.track('mobile_sort');
    setMobileSortOpen(false);
    handleMobileOrderChange(sort);
  };

  const handleClearFilter = key => {
    analytics.track('filters_clear');
    setValue(key, '');
    push({
      location: location.pathname,
      search: stringify(omit(query, [key]))
    });
  };

  return (
    <FilterOrdersComponent
      filterKey={ filterKey }
      register={ register }
      control={ control }
      getValues={ getValues }
      setValue={ setValue }
      onSubmit={ handleSubmit }
      onClearFilters={ handleClearFilters }
      onClearFilter={ handleClearFilter }
      onChangeDate={ handleDateChange }
      states={ states }
      types={ types }
      deliveryStockOptions={ deliveryStockOptions }
      users={ users }
      usersLoading={ usersLoading }
      startDate={ startDate }
      endDate={ endDate }
      activeFilters={ omit(query || {}, ['order_by', 'order_direction', 'page']) }
      mobileFiltersOpen={ mobileFiltersOpen }
      setMobileFiltersOpen={ setMobileFiltersOpen }
      mobileSortOpen={ mobileSortOpen }
      setMobileSortOpen={ setMobileSortOpen }
      sortOptions={ sortOptions }
      currentSort={ currentSort }
      handleMobileOrderChange={ onMobileOrderChange }
      isExternalReseller={ isExternalReseller }
    />
  );
});

OrdersFilters.propTypes = {
  sortOptions: array,
  currentSort: object,
  handleMobileOrderChange: func,
};

OrdersFilters.displayName = 'OrdersFiltersContainer';

export default OrdersFilters;
